import { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { Send, RefreshCw, Loader2, Mail, Inbox } from "lucide-react";

export function Emails({ data, canManage, reference, reload }) {
  const [form, setForm] = useState({ direction: "sent", subject: "", body: "" });
  const [gmail, setGmail] = useState(null);
  const [fromAddr, setFromAddr] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!canManage) return;
    api.get("/admin/gmail/status").then(r => {
      setGmail(r.data);
      if (r.data.connected) setFromAddr(r.data.default_from || "");
      if (r.data.last_inbound_sync) setLastSync(r.data.last_inbound_sync);
    }).catch(() => {});
  }, [canManage]);

  useEffect(() => {
    if (!reference) return;
    api.post(`/admin/booking/${reference}/emails/mark-read`).catch(() => {});
  }, [reference]);

  const save = async () => {
    if (!form.subject.trim() || !form.body.trim()) { toast.error("Subject and body required"); return; }
    try {
      const r = await api.post(`/admin/booking/${reference}/email`, { ...form, from_addr: fromAddr || undefined });
      toast.success(form.direction === "sent" ? (r.data.sent ? `Sent from ${r.data.from || fromAddr}` : "Logged (email sending not configured)") : "Logged");
      setForm({ direction: "sent", subject: "", body: "" }); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const refreshInbox = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/gmail/sync-inbound");
      if (r.data.reason === "gmail_not_connected") toast.error("Connect Gmail in Settings to sync inbound emails");
      else toast.success(r.data.synced > 0 ? `Filed ${r.data.synced} new client email${r.data.synced > 1 ? "s" : ""}` : "No new client emails");
      if (r.data.last_sync) setLastSync(r.data.last_sync);
      reload();
    } catch { toast.error("Could not refresh inbox"); }
    setSyncing(false);
  };

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));
  const [filter, setFilter] = useState("all");
  const allEmails = data.emails || [];
  const isReceived = (e) => e.direction === "received";
  const counts = {
    all: allEmails.length,
    received: allEmails.filter(isReceived).length,
    sent: allEmails.filter((e) => !isReceived(e)).length,
  };
  const emails = filter === "all" ? allEmails : allEmails.filter((e) => (filter === "received" ? isReceived(e) : !isReceived(e)));

  return (
    <div data-testid="jobfile-emails">
      {/* Inbound sync header */}
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <p className="text-xs text-[#6B7280] flex items-center gap-1.5">
          <Inbox size={13} /> Client replies auto-file here every hour.
          {lastSync && <span className="text-[#9CA3AF]"> Last checked {new Date(lastSync).toLocaleString("en-GB")}.</span>}
        </p>
        {canManage && (
          <button data-testid="emails-refresh-inbox" onClick={refreshInbox} disabled={syncing}
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest border border-[#E5E7EB] px-3 py-1.5 hover:border-[#C79A3B] disabled:opacity-60">
            {syncing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Refresh inbox
          </button>
        )}
      </div>

      {canManage && (
        <div className="border border-[#E5E7EB] bg-white p-3 mb-4">
          <div className="flex gap-2 mb-2 flex-wrap items-center">
            {[["sent", "Send to client"], ["logged", "Log sent"], ["received", "Log received"]].map(([v, l]) => (
              <button key={v} data-testid={`email-dir-${v}`} onClick={() => setForm({ ...form, direction: v })} className={`px-3 py-1 text-xs font-bold uppercase border ${form.direction === v ? "bg-[#12263A] text-white border-[#12263A]" : "border-[#E5E7EB]"}`}>{l}</button>
            ))}
            {form.direction === "sent" && gmail?.connected && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="label-eyebrow">From</span>
                <select data-testid="jobfile-email-from" value={fromAddr} onChange={e => setFromAddr(e.target.value)} className="border border-[#E5E7EB] px-2 py-1 text-sm">
                  {(gmail.from_options?.length ? gmail.from_options : [{ email: gmail.default_from, default: true }]).map(o => (
                    <option key={o.email} value={o.email}>{o.email}{o.default ? " (default)" : ""}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <input data-testid="email-subject" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Subject" className="border border-[#E5E7EB] px-3 py-2 text-sm w-full mb-2" />
          <textarea data-testid="email-body" value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={4} placeholder="Message" className="border border-[#E5E7EB] px-3 py-2 text-sm w-full mb-2" />
          <button data-testid="email-save" onClick={save} className="de-btn-primary"><Send size={16} /> {form.direction === "sent" ? "Send & log" : "Log email"}</button>
          {form.direction === "sent" && gmail && !gmail.connected && <p className="text-xs text-[#6B7280] mt-2">Connect Gmail in Settings → Connected email to choose a sender address.</p>}
        </div>
      )}

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {[["all", "All"], ["received", "Received"], ["sent", "Sent"]].map(([k, label]) => (
          <button key={k} data-testid={`emails-filter-${k}`} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border ${filter === k ? "bg-[#12263A] text-white border-[#12263A]" : "border-[#E5E7EB] text-[#6B7280]"}`}>
            {label} <span className="ml-1 opacity-70">{counts[k]}</span>
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {emails.map(e => {
          const body = e.body || "";
          const long = body.length > 280;
          const open = !!expanded[e.id];
          const received = e.direction === "received";
          return (
            <div key={e.id} data-testid={`email-${e.id}`} className={`border bg-white p-3 ${received ? "border-l-4 border-l-[#12263A] border-[#E5E7EB]" : "border-[#E5E7EB]"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-sm flex items-center gap-1.5">
                  <Mail size={13} className="text-[#9CA3AF]" /> {e.subject}
                </span>
                <span className="flex items-center gap-1.5">
                  {e.via === "gmail" && <span className="text-[9px] uppercase tracking-widest font-bold bg-[#F1F3F5] text-[#6B7280] px-1.5 py-0.5 rounded-sm">Gmail</span>}
                  <span className="text-[10px] uppercase tracking-widest text-[#C79A3B] font-bold">{e.direction}</span>
                </span>
              </div>
              <button data-testid={`email-toggle-${e.id}`} onClick={() => long && toggle(e.id)} className={`text-sm text-[#6B7280] mt-1 whitespace-pre-wrap text-left w-full ${long ? "cursor-pointer" : "cursor-default"}`}>
                {open || !long ? body : body.slice(0, 280).trimEnd() + "…"}
              </button>
              {long && (
                <button data-testid={`email-showmore-${e.id}`} onClick={() => toggle(e.id)} className="text-xs font-bold text-[#C79A3B] mt-1">
                  {open ? "Show less" : "Read full email"}
                </button>
              )}
              <div className="text-xs text-[#6B7280] mt-1">{e.from ? `from ${e.from} · ` : ""}{e.to ? `to ${e.to}` : ""} · {new Date(e.created_at).toLocaleString("en-GB")}{e.sent ? " · sent" : ""}{e.author === "auto" ? " · auto-filed" : ""}</div>
            </div>
          );
        })}
        {emails.length === 0 && <div className="text-sm text-[#6B7280]">{filter === "all" ? "No email correspondence logged yet." : `No ${filter} emails.`}</div>}
      </div>
    </div>
  );
}
